// Layer 3 (Transformations): the next-day plan tab — the plan for the
// production day after the session cursor, read from the seeded targets and
// the [syn] roster. paul-tarp.A20 (#422), the `a20-meeting` stream, slice S2b.
//
// This is a plan, not a forecast. Every figure below is either a seeded
// comparator (a reading's own `target`) or the two-product budget identity
// over one (`concentrateBudgetTd`, the same helper the `24 h performance` tab
// judges its concentrate rows with), and the crews come from the roster's own
// lookup. Nothing here projects a shift series forward — that read is out of
// this pass (module map § 7.7).
//
// Day and night are equal by construction: the budget is one daily figure and
// `rd-kpi-milled-day.design` equals `rd-kpi-milled-night.design`, so a
// per-shift figure renders once beside its daily figure, never as two equal
// columns (module map § 7.6–7.7).
//
// No React, no engine mutations — every function takes state in and returns
// data out.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import {
  BUNKER_HILL_SEATS,
  PRIMARY_SEAT_BY_ROLE,
  activeCrewForShift,
  crewLabelById,
} from '@/versions/v1/shared/bunker-hill-org-seed';
import {
  formatCursorBound,
  productionDayOf,
  productionDayShiftWindows,
  type ShiftWindow,
} from '@/versions/v1/shared/clock.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { concentrateBudgetTd, formatFigure } from './PerfTab.transform';
import { MEETING_CURSOR, THREE_W_ENTRIES, type ThreeWEntry } from './seed';
import {
  formatCommitDate,
  isoAddDays,
  threeWSortRank,
  threeWStatus,
  type ThreeWStatus,
} from './Scene.transform';

/** The next production day's own shift windows. `isoAddDays` is fed the
 * production day's *calendar date* — it parses `YYYY-MM-DD`, so handing it the
 * cursor's `SeriesTime` throws. Both windows always exist regardless of data,
 * which is what a plan needs (`clock.derive.ts`). */
function nextDayWindows(state: SessionState): { night: ShiftWindow; day: ShiftWindow } {
  const next = isoAddDays(productionDayOf(state.cursor).date, 1);
  return productionDayShiftWindows(next as SeriesTime);
}

export type NextDayPlanWindow = {
  start: SeriesTime;
  end: SeriesTime;
  /** `08 Jun 18:00 → 09 Jun 18:00` — the plant's production day, 18:00→18:00. */
  label: string;
};

/** The window the plan covers: the whole production day after the cursor, from
 * its night shift's start to its day shift's end. The bounds come from
 * `productionDayShiftWindows` and are rendered by `formatCursorBound`, never
 * typed. */
export function nextDayPlanWindow(state: SessionState): NextDayPlanWindow {
  const w = nextDayWindows(state);
  const start = w.night.start;
  const end = w.day.end;
  return { start, end, label: `${formatCursorBound(start)} → ${formatCursorBound(end)}` };
}

/** The role seat that owns the plan, by label alone. The org seed now names
 * K. Brennan in `seat-supt`; this role-owned line continues to show the seat
 * label rather than a person-specific assignment. */
export function nextDayPlanOwner(): string {
  return (
    BUNKER_HILL_SEATS.find((seat) => seat.id === PRIMARY_SEAT_BY_ROLE.superintendent)?.label ??
    'Superintendent'
  );
}

export type NextDayPlanRow = { id: string; label: string; plan: string };

/** The eight plan lines, in reading order. Tonnage (with its exact half per
 * shift), feed rate, the three recoveries and mill utilisation are the seeded
 * budget figures for the coming 24 h; the two concentrate masses are today's
 * daily budget carried forward —
 * `concentrateBudgetTd` reads the window *elapsed* up to the cursor, so a
 * next-day `from` would return 0, and the plan the room writes is the same
 * daily identity (recorded assumption, D7). */
export function nextDayPlanRows(state: SessionState): readonly NextDayPlanRow[] {
  const oreTarget = readingByIdLookup('rd-kpi-ore-td')?.target ?? 0;
  const dayFrom = productionDayOf(state.cursor).start;
  const pb = concentrateBudgetTd('Pb', state, dayFrom);
  const zn = concentrateBudgetTd('Zn', state, dayFrom);
  const atLeast = (readingId: string) =>
    `≥ ${formatFigure(readingByIdLookup(readingId)?.target ?? 0, 1)} %`;

  return [
    {
      id: 'tonnes',
      label: 'Tonnes milled',
      plan: `${formatFigure(oreTarget, 0)} t · ${formatFigure(oreTarget / 2, 0)} per shift`,
    },
    { id: 'feed-rate', label: 'Mill feed rate', plan: `${formatFigure(oreTarget / 24, 1)} t/h` },
    // Daily figure only: a rounded half never adds back to a rounded whole
    // (37.5 t is 2 × 18.74), and the tonnage row already shows the split.
    { id: 'pb-con', label: 'Pb/Ag con produced', plan: `${formatFigure(pb, 1)} t` },
    { id: 'zn-con', label: 'Zn con produced', plan: `${formatFigure(zn, 1)} t` },
    { id: 'pb-rec', label: 'Pb recovery', plan: atLeast('rd-kpi-pb-rec') },
    { id: 'zn-rec', label: 'Zn recovery', plan: atLeast('rd-kpi-zn-rec') },
    { id: 'ag-rec', label: 'Ag recovery', plan: atLeast('rd-kpi-ag-rec') },
    { id: 'mill-util', label: 'Mill utilisation', plan: atLeast('rd-kpi-mill-util') },
  ];
}

export type NextDayCrew = {
  kind: 'night' | 'day';
  crewLabel: string;
  /** `Night shift · 18:00–06:00` — the window's own label. */
  windowLabel: string;
  headcount: number;
  seniors: number;
  shortStaffed: boolean;
};

/** The two crews the plan is handed to, night first — the production day runs
 * night then day. `shortStaffed` carries the same rule `handoverCrew` uses
 * (`headcount < 6`), so one crew reads the same on both tabs. */
export function nextDayCrews(state: SessionState): readonly NextDayCrew[] {
  const w = nextDayWindows(state);
  const build = (kind: 'night' | 'day'): NextDayCrew => {
    const roster = activeCrewForShift(w[kind].start, kind);
    return {
      kind,
      crewLabel: crewLabelById(roster.crewId),
      windowLabel: w[kind].label,
      headcount: roster.headcount,
      seniors: roster.seniors,
      shortStaffed: roster.headcount < 6,
    };
  };
  return [build('night'), build('day')];
}

export type NextDayPriority = ThreeWEntry & {
  /** The committed date, formatted, or null where the row carries none —
   * `formatCommitDate(null)` renders an em dash, which this route does not
   * show. */
  commitDate: string | null;
  /** The register's own status word for the row; the plan shows it beside the
   * date only where the date alone would mislead (overdue, no new date). */
  status: ThreeWStatus;
};

/** The register rows the coming day is planned against: open rows with no
 * committed date or one inside the plan window, ranked by the same
 * `threeWSortRank` the register sorts on — so the plan and the register can
 * never order one row differently. The list is shared under both crew panels:
 * `ThreeWEntry` carries no shift field, so no row can honestly be attributed
 * to one crew (module map § 7.7). */
export function nextDayPriorities(): readonly NextDayPriority[] {
  const cutoff = isoAddDays(MEETING_CURSOR, 1);
  return THREE_W_ENTRIES.filter(
    (row) => !row.doneAwaitingAck && (row.currentCommit === null || row.currentCommit <= cutoff),
  )
    .slice()
    .sort((a, b) => threeWSortRank(a) - threeWSortRank(b) || a.id.localeCompare(b.id))
    .map((row) => ({
      ...row,
      commitDate: row.currentCommit ? formatCommitDate(row.currentCommit) : null,
      status: threeWStatus(row),
    }));
}
